// work/proposals/redlining/_schemas/proposal-field-schema.ts
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import { ProposalFieldCategory, ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import { z } from "zod";

export type ProposalFieldJSON = z.infer<typeof ProposalFieldSchema.ProposalFieldJSONZ>;
export type ProposalFieldParams = z.infer<typeof ProposalFieldSchema.ProposalFieldZ>;

export default class ProposalFieldSchema {
  public static readonly ProposalFieldJSONZ = z.union([
    z.object({
      name: z.string(),
      category: z.string(),
      id: z.uuid().optional(),
      get commentFieldOverride() {
        return z
          .union([
            z.object({ name: z.string(), category: z.string(), id: z.uuid().optional() }),
            z.templateLiteral(["general"]),
            z.null(),
          ])
          .optional();
      },
    }),
    z.templateLiteral(["general"]),
  ]);

  public static readonly ProposalFieldZ = z.union([
    z.object({
      name: z.enum(ProposalFieldName),
      category: z.enum(ProposalFieldCategory),
      id: z.instanceof(Guid).optional(),
      get commentFieldOverride() {
        return z
          .union([
            z.object({
              name: z.enum(ProposalFieldName),
              category: z.enum(ProposalFieldCategory),
              id: z.instanceof(Guid).optional(),
            }),
            z.templateLiteral(["general"]),
            z.null(),
          ])
          .optional();
      },
    }),
    z.templateLiteral(["general"]),
  ]);

  public static validate(values: unknown): ProposalFieldParams {
    return parseOrThrow(ProposalFieldSchema.ProposalFieldZ, values, {
      logError: true,
      schemaName: "ProposalFieldSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): ProposalFieldJSON {
    return parseOrThrow(ProposalFieldSchema.ProposalFieldJSONZ, values, {
      logError: true,
      schemaName: "ProposalFieldSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is ProposalFieldParams {
    try {
      ProposalFieldSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is ProposalFieldJSON {
    try {
      ProposalFieldSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: ProposalFieldJSON): ProposalFieldParams {
    const parsed = parseOrThrow(ProposalFieldSchema.ProposalFieldJSONZ, data, {
      logError: true,
      schemaName: "ProposalFieldSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return ProposalFieldSchema.codec.decode(parsed);
  }

  public static serialize(params: ProposalFieldParams): ProposalFieldJSON {
    const parsed = ProposalFieldSchema.validate(params);
    return ProposalFieldSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(ProposalFieldSchema.ProposalFieldJSONZ, ProposalFieldSchema.ProposalFieldZ, {
    decode: (data) => {
      if (isNullOrUndefined(data) || data === "general") return data;

      return {
        name: data.name as ProposalFieldName,
        category: data.category as ProposalFieldCategory,
        id: Guid.fromJSON(data.id) ?? undefined,
        commentFieldOverride: data.commentFieldOverride
          ? ProposalFieldSchema.deserialize(data.commentFieldOverride)
          : undefined,
      };
    },
    encode: (value) => {
      if (value === "general") return value;

      let commentFieldOverride: ProposalFieldJSON | undefined;
      if (isNullOrUndefined(value.commentFieldOverride) || value.commentFieldOverride === "general") {
        commentFieldOverride = value.commentFieldOverride ?? undefined;
      } else {
        commentFieldOverride = ProposalFieldSchema.serialize(value.commentFieldOverride);
      }

      return {
        name: value.name,
        category: value.category,
        id: value.id?.toJSON(),
        commentFieldOverride,
      };
    },
  });
}
